import { applyIntentToDB, getDefaultTask, getSnapshot, insertSnapshot } from "./Domain";


export type Task = {
  id: string,
  title:string,
  description:string,
  priority: number,
  dependsOn: string[],
  status?: string,
  isComplete: boolean,
  isBlocked: boolean,
  isFullyResolved: boolean,
  isExternal: boolean,
  dueDate?: string,
  createdDate?: string,
  doneDate?: string,

}

export type Filter = {
  blocked: boolean
  complete: boolean
  external: boolean
  resolved?: boolean
  overdue?: boolean
  rejectAll?: boolean
}

export type TaskMap = Record<string, Task>

export function passes(t: Task, f : Filter) {
  if (f.rejectAll) return false

  return (
    (f.external || !t.isExternal) &&
    (f.blocked  || !t.isBlocked)  &&
    (f.complete || !t.isComplete) &&
    ((f.resolved ?? true) || !t.isFullyResolved)
  )
}

//const SERVER_PATH = "http://localhost:8000"
//const SERVER_PATH = ""
export const getTasks = getSnapshot;
export const saveTasks = insertSnapshot;

export function calculate(tasks : TaskMap) : TaskMap {
  const next: TaskMap = {}

  /** Checks for deep completeness--the task and all its parents must be complete */
  function isComplete(task: Task) {
    //if (task.status != 'complete') return false;
    if (!task.isComplete) return false;

    for (const dep of task.dependsOn) {
      if (!isComplete(tasks[dep])) return false;
    }
    return true;

  }

  console.debug("Pre-calculation:", tasks)
  for (const [id, t] of Object.entries(tasks)) {
    let isBlocked = false;
    let status = t.status;
    let taskIsComplete = t.isComplete;
    t.dependsOn = Array.from(new Set(t.dependsOn))
    for (const dep of t.dependsOn) {
      //if (tasks[dep].status !== 'complete') {
      if (!isComplete(tasks[dep])) {
        isBlocked = true;
        status = 'not started';
        taskIsComplete = false;
        break;
      }
    }

    const isExternal = t.isExternal ?? false

    next[id] = {
      ...t,
      isBlocked: isBlocked,
      isExternal: isExternal,
      status: status,
      isComplete: taskIsComplete
    };

  }

  // --- reverse adjacency: id -> ids that depend on it ---
  const dependents: Record<string, string[]> = {};
  for (const id of Object.keys(next)) dependents[id] = [];
  for (const [id, t] of Object.entries(next)) {
    for (const dep of t.dependsOn) {
      if (!dependents[dep]) dependents[dep] = []; // dep may not exist in `next`; harmless, never read back
      dependents[dep].push(id);
    }
  }

  // --- second pass: isFullyResolved, memoized, cycle-safe ---
  const resolvedMemo: Record<string, boolean> = {};
  const resolving = new Set<string>();

  function isFullyResolved(id: string): boolean {
    if (id in resolvedMemo) return resolvedMemo[id];
    if (resolving.has(id)) return false; // cycle guard: break rather than recurse forever
    resolving.add(id);

    let result = next[id].isComplete;
    if (result) {
      for (const childId of dependents[id]) {
        if (!isFullyResolved(childId)) { result = false; break; }
      }
    }

    resolving.delete(id);
    resolvedMemo[id] = result;
    return result;
  }

  for (const id of Object.keys(next)) {
    next[id] = { ...next[id], isFullyResolved: isFullyResolved(id) };
  }

  window['tasks'] = next;
  console.debug("Recalculated:", next);
  return next
}

export type CommitEvent =
| { id: string; type: 'block'; blockerId: string }
| { id: string; type: 'unblock'; blockerId: string }
| { id: string; type: 'add', task?}
| { id: string; type: 'delete' }
| { [K in keyof Task]: {
      id: string;
      type: 'update';
      field: K;
      value: Task[K];
      task?: Task;
    }
  }[keyof Task]

export function processIntent(event: CommitEvent, prev: TaskMap) : TaskMap {
  const t = prev[event.id];

  console.debug('Executing event:', event, 'on task', event.id, ':', t);
  //console.debug('There is event', event);

  switch (event.type) {
    case 'block': {
      if (event.blockerId == t.id) {return prev;} // don't block yourself
      return {...prev, [event.id]: {...t, dependsOn: [...t.dependsOn, event.blockerId]}}
    }
    case 'unblock': {
      if (event.blockerId == t.id) {return prev;} // don't block yourself
      return {...prev, [event.id]: {...t, dependsOn: t.dependsOn.filter(id => id != event.blockerId)}}
    }
    case "add": {
      const def = getDefaultTask(event.id);
      const today = new Date().toISOString().slice(0, 10);
      const newTasks = {...prev, [def.id]: {...def, ...event.task, createdDate: event.task?.createdDate ?? today}}
      console.log('Commit new tasks including ', def, newTasks);

      applyIntentToDB({...event, task: {...def, ...event.task}})
      return newTasks;
    }
    case "delete": {
      delete prev[event.id];
      for (const childID of Object.keys(prev)) {
        // Remove from dependencies
        prev[childID].dependsOn = prev[childID].dependsOn.filter(v => v != event.id)
      }
      return prev
    }

    case "update": {
      if (event.field == 'isComplete' && t.isBlocked) {
        console.warn('commit fails', event, t);
        return prev;
      }

      const today = new Date().toISOString().slice(0, 10);
      const extra: Partial<Task> = event.field === 'isComplete'
        ? { doneDate: event.value ? today : undefined }
        : {}

      applyIntentToDB({...event, task: {...t, ...event.task, [event.field]: event.value, ...extra}})

      return { ...prev, [event.id]: { ...t, [event.field]: event.value, ...extra } }

    }

    default: return prev;
  }

}

export function exportMarkdown(tasks: TaskMap) : string {

  let final: string[] = []

  for (const [_,task] of Object.entries(tasks)) {
    let char : string = task.isComplete ? 'x' : ' ';
    final.push(`- [${char}] ${task.title} <${task.id}>`)

  }

  return final.join('\n')

}

export function taskAsMarkdown(t: Task | string, map: TaskMap): string {
  if (typeof t == 'string') t = map[t];

  return printTask(t, structuredClone(map))
}

export function printTask(t: Task, map: TaskMap, tabs: number = 0) : string {
  //let m = structuredClone(map)
  if (t == null) {return ''}
  let char : string = t.isComplete ? 'x' : ' ';
  let prefix : string = '\t'.repeat(tabs)
  let top = `${prefix}- [${char}] ${t.title} <${t.id}>\n`

  map[t.id] = null

  t.dependsOn.forEach(depID => { top = top + '' + printTask(map[depID], map, tabs + 1) })

  return top
}

// console.log(exportMarkdown(tasks))
// console.log(printTask(tasks['yLWhVTG7'], structuredClone(tasks), 0))

window['exportMarkdown'] = exportMarkdown
window['printTask'] = taskAsMarkdown
